import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/app/api/client';
import { toast } from 'react-hot-toast';
import { Camera, Loader2, Trash2, Globe, Building } from 'lucide-react';
import { apiConfig } from '@/config/api';
import FormInput from '@/components/common/form/FormInput';

import { useAuth } from '@/context/AuthContext';

export const OrganizationProfileSection = () => {
    const { refreshUserStatus } = useAuth();
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [businessName, setBusinessName] = useState('');
    const [website, setWebsite] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get('/api/tenant/settings');
            if (response.data) {
                if (response.data.logoUrl) {
                    const url = apiConfig.getUrl(response.data.logoUrl);
                    // Use R2 key as version generator if available
                    const version = response.data.logoR2Key ? `?v=${encodeURIComponent(response.data.logoR2Key)}` : '';
                    setLogoUrl(url + version);
                }
                setBusinessName(response.data.businessName || '');
                setWebsite(response.data.website || '');
            }
        } catch (error) {
            console.error('Failed to fetch tenant settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveInfo = async () => {
        setIsSaving(true);
        try {
            // We need to fetch current settings first to merge, or backend handles partial?
            // tenant-api settings update usually replaces or merges?
            // Backend uses `JSON.parse(tenantData.settings || {})` then merges?
            // Let's check backend logic. `tenantApi.put('/settings')` does:
            // `body = c.req.valid('json')`. And DO `settings/...` PUT replaces?
            // Looking at tenant-api.js logic... line 79 passes `JSON.stringify(body)`.
            // It implies a replace or merge depending on DO implementation.
            // Safest to fetch current settings? Or just send fields we know.
            // We'll trust the backend handles merging if implemented well, or we fetch & merge.
            // Let's do a fetch-first to be safe similar to profile page.

            // Fetch current settings first to merge safety
            const currentSettingsRes = await apiClient.get('/api/tenant/settings');
            const currentSettings = currentSettingsRes.data || {};

            await apiClient.put('/api/tenant/settings', {
                ...currentSettings,
                businessName,
                website
            });
            toast.success('Organization info updated');
            setIsEditing(false);
        } catch (error: any) {
            console.error('Failed to save settings:', error);
            toast.error('Failed to update organization info');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        // Optimized: We could refetch or just keep current state if we want to support cancelling edits
        fetchSettings();
    };

    const handleLogoClick = () => {
        if (!isEditing) return; // Only allow upload in edit mode? Or allow in both?
        // User asked for "Preview mode and edit mode". Usually file upload is an edit action.
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds 2MB limit');
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('logo', file);

        try {
            const response = await apiClient.post('/api/tenant/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Use the returned R2Key for smart versioning, fallback to timestamp if not present
            const version = response.data.logoR2Key
                ? `?v=${encodeURIComponent(response.data.logoR2Key)}`
                : `?t=${Date.now()}`;

            const newLogoUrl = apiConfig.getUrl(response.data.logoUrl) + version;
            setLogoUrl(newLogoUrl);
            await refreshUserStatus();
            toast.success('Business logo updated');
        } catch (error: any) {
            console.error('Error uploading logo:', error);
            toast.error(error.response?.data?.error || 'Failed to upload logo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveLogo = async () => {
        if (!confirm('Are you sure you want to remove the business logo?')) return;
        setIsUploading(true);
        try {
            const currentSettingsRes = await apiClient.get('/api/tenant/settings');
            const currentSettings = currentSettingsRes.data || {};

            await apiClient.put('/api/tenant/settings', {
                ...currentSettings,
                logoUrl: null,
                logoR2Key: null
            });
            setLogoUrl(null);
            await refreshUserStatus();
            toast.success('Business logo removed');
        } catch (error: any) {
            console.error('Error removing logo:', error);
            toast.error('Failed to remove logo');
        } finally {
            setIsUploading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header / Edit Toggle */}
            <div className="flex justify-between items-center mb-2">
                <div>
                    {/* Header handled by ProfileSection, but we can add sub-label here if needed */}
                </div>
                {!isEditing && (
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Edit Organization
                    </button>
                )}
            </div>

            {isEditing ? (
                /* EDIT MODE */
                <div className="space-y-6">
                    <div className="flex items-start gap-6">
                        {/* Logo Uploader */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">Logo</label>
                            <div className="relative group flex-shrink-0">
                                <div
                                    className={`w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors ${isUploading ? 'opacity-50' : ''}`}
                                    onClick={handleLogoClick}
                                >
                                    {logoUrl ? (
                                        <img src={logoUrl} alt="Business Logo" className="w-full h-full object-contain" />
                                    ) : (
                                        <div className="text-center p-2">
                                            <Camera className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                                            <span className="text-xs text-gray-500">Upload</span>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-1.5 shadow-sm">
                                            <Camera className="w-4 h-4 text-gray-600" />
                                        </div>
                                    </div>
                                </div>
                                {isUploading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                    </div>
                                )}
                            </div>
                            {logoUrl && (
                                <button type="button" onClick={handleRemoveLogo} disabled={isUploading} className="text-xs text-red-600 hover:underline">
                                    Remove Logo
                                </button>
                            )}
                        </div>

                        <div className="flex-1 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                                <FormInput
                                    type="text"
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    placeholder="e.g. Acme Corp"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                                <FormInput
                                    type="url"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveInfo}
                            disabled={isSaving}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save Info'}
                        </button>
                    </div>
                </div>
            ) : (
                /* PREVIEW MODE */
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
                    <div className="w-20 h-20 bg-white rounded-lg border border-gray-200 flex items-center justify-center p-2 shadow-sm">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Organization Logo" className="max-w-full max-h-full object-contain" />
                        ) : (
                            <Building className="w-8 h-8 text-gray-300" />
                        )}
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Legal Name</dt>
                                <dd className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                    {businessName || <span className="text-gray-400 italic">Not set (Defaults to tenant name)</span>}
                                </dd>
                            </div>

                            <div>
                                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Official Website</dt>
                                <dd className="text-sm text-gray-900">
                                    {website ? (
                                        <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                            <Globe className="w-3.5 h-3.5" />
                                            {website}
                                        </a>
                                    ) : (
                                        <span className="text-gray-400 italic flex items-center gap-1">
                                            <Globe className="w-3.5 h-3.5" /> Not set
                                        </span>
                                    )}
                                </dd>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
            />
        </div>
    );
};

export default OrganizationProfileSection;
